import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField, 
  Button, 
  Alert,
  Grid,
  Divider,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { 
  Settings as SettingsIcon, 
  Storage as BackupIcon, 
  Download as DownloadIcon,
  Restore as RestoreIcon,
  Upload as UploadIcon,
  Email as EmailIcon,
  Send as SendIcon
} from '@mui/icons-material';
import api from '../services/api';

const SystemSettings = () => {
  const [settings, setSettings] = useState({
    current_semester: '',
    current_academic_year: '',
    eval_professional_open: '1',
    eval_specialist_open: '1',
    eval_coteacher_open: '1',
    demand_survey_open: '1',
    demand_survey_start_date: '',
    demand_survey_end_date: '',
    exam_geofence_global_enabled: '1',
    smtp_host: 'smtp.gmail.com',
    smtp_port: '587',
    smtp_secure: '0',
    smtp_user: '',
    smtp_pass: '',
    smtp_from: 'TPMS KPRU <no-reply@tp.kpru.ac.th>',
    app_url: 'https://tp.kpru.ac.th'
  });
  const [backups, setBackups] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [restoring, setRestoring] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedBackupToRestore, setSelectedBackupToRestore] = useState('');
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState<{ type: 'success' | 'error' | ''; text: string }>({ type: '', text: '' });

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      // Filter out the old key if it comes from the backend
      const filtered = Object.keys(response.data)
        .filter(key => key !== 'required_teaching_hours')
        .reduce((obj: any, key) => {
          obj[key] = response.data[key];
          return obj;
        }, {});
      setSettings(prev => ({ ...prev, ...filtered }));
    } catch (err) {
      console.error('Failed to fetch settings', err);
    }
  };

  const fetchBackups = async () => {
    try {
      const response = await api.get('/settings/backups');
      setBackups(response.data);
    } catch (err) {
      console.error('Failed to fetch backups', err);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchBackups();
  }, []);

  const handleChange = (e: any) => {
    const value = e.target.type === 'checkbox' ? (e.target.checked ? '1' : '0') : e.target.value;
    setSettings({ ...settings, [e.target.name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.put('/settings', settings);
      setMessage({ type: 'success', text: 'บันทึกการตั้งค่าระบบเรียบร้อยแล้ว' });
    } catch (err: any) {
      setMessage({ type: 'error', text: 'ไม่สามารถบันทึกการตั้งค่าได้' });
    }
  };

  const handleCreateBackup = async () => {
    setMessage({ type: '', text: '' });
    try {
      await api.post('/settings/backup');
      setMessage({ type: 'success', text: 'สำรองข้อมูลฐานข้อมูลเรียบร้อยแล้ว' });
      fetchBackups();
    } catch (err: any) {
      console.error(err);
      setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาดในการสำรองข้อมูล' });
    }
  };

  const handleRunAutoBackup = async () => {
    setMessage({ type: 'info', text: 'กำลังเริ่มกระบวนการสำรองข้อมูลอัตโนมัติประจำวัน...' });
    try {
      await api.post('/settings/run-auto-backup');
      setMessage({ type: 'success', text: 'ดำเนินการสำรองข้อมูลอัตโนมัติประจำวันเรียบร้อยแล้ว' });
      fetchBackups();
    } catch (err: any) {
      console.error(err);
      setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาดในการสำรองข้อมูล' });
    }
  };

  const handleConfirmRestore = (fileName: string) => {
    setSelectedBackupToRestore(fileName);
    setRestoreDialogOpen(true);
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress || !testEmailAddress.trim()) {
      setTestEmailResult({ type: 'error', text: 'กรุณาระบุอีเมลปลายทางสำหรับทดสอบ' });
      return;
    }

    setTestEmailLoading(true);
    setTestEmailResult({ type: '', text: '' });

    try {
      const res = await api.post('/settings/test-smtp', {
        test_email: testEmailAddress.trim(),
        smtp_host: settings.smtp_host,
        smtp_port: settings.smtp_port,
        smtp_secure: settings.smtp_secure,
        smtp_user: settings.smtp_user,
        smtp_pass: settings.smtp_pass,
        smtp_from: settings.smtp_from
      });
      setTestEmailResult({ type: 'success', text: res.data.message || 'ส่งอีเมลทดสอบสำเร็จเรียบร้อยแล้ว' });
    } catch (err: any) {
      setTestEmailResult({ type: 'error', text: err.response?.data?.message || 'การส่งอีเมลทดสอบล้มเหลว' });
    } finally {
      setTestEmailLoading(false);
    }
  };

  const handleExecuteRestore = async () => {
    setRestoreDialogOpen(false);
    setRestoring(true);
    setMessage({ type: 'info', text: 'กำลังกู้คืนฐานข้อมูล กรุณารอสักครู่ (กรุณาอย่าปิดหน้าต่างนี้)...' });
    try {
      await api.post(`/settings/restore/${selectedBackupToRestore}`);
      setMessage({ type: 'success', text: `คืนค่าข้อมูลจากไฟล์ "${selectedBackupToRestore}" สำเร็จเรียบร้อยแล้ว` });
    } catch (err: any) {
      console.error(err);
      setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาดในการคืนค่าข้อมูล' });
    } finally {
      setRestoring(false);
    }
  };

  const handleUploadRestore = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const confirm = window.confirm(`คุณต้องการอัปโหลดและกู้คืนฐานข้อมูลจากไฟล์ "${file.name}" ใช่หรือไม่?\n\nคำเตือน: ข้อมูลปัจจุบันทั้งหมดจะถูกเขียนทับด้วยข้อมูลจากไฟล์สำรองนี้ทันที!`);
    if (!confirm) {
      e.target.value = '';
      return;
    }

    setRestoring(true);
    setMessage({ type: 'info', text: `กำลังอัปโหลดและกู้คืนฐานข้อมูลจากไฟล์ "${file.name}"...` });
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('/settings/restore-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setMessage({ type: 'success', text: `อัปโหลดและคืนค่าข้อมูลจากไฟล์ "${file.name}" สำเร็จเรียบร้อยแล้ว` });
      fetchBackups();
    } catch (err: any) {
      console.error(err);
      setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาดในการคืนค่าข้อมูล' });
    } finally {
      setRestoring(false);
      e.target.value = '';
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SettingsIcon sx={{ mr: 1, fontSize: { xs: 24, sm: 30 } }} />
        <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>ตั้งค่าระบบ (System Settings)</Typography>
      </Box>

      {message.text && (
        <Alert 
          severity={message.type as any} 
          sx={{ mb: 3 }}
          icon={restoring ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>การตั้งค่าปีการศึกษาและเกณฑ์วิชาการ</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          การตั้งค่าส่วนนี้จะส่งผลต่อการจับคู่อัตโนมัติ (Auto-match) และเกณฑ์การผ่านหลักสูตรของนักศึกษา
        </Typography>
        <Divider sx={{ mb: 4 }} />

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                select
                fullWidth
                label="ภาคเรียนปัจจุบัน"
                name="current_semester"
                value={settings.current_semester}
                onChange={handleChange}
                disabled={restoring}
              >
                <MenuItem value="1">ภาคเรียนที่ 1</MenuItem>
                <MenuItem value="2">ภาคเรียนที่ 2</MenuItem>
                <MenuItem value="3">ภาคเรียนฤดูร้อน</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="ปีการศึกษาปัจจุบัน (พ.ศ.)"
                name="current_academic_year"
                value={settings.current_academic_year}
                onChange={handleChange}
                placeholder="เช่น 2569"
                disabled={restoring}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>สถานะการเปิด/ปิด รอบการประเมิน</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControlLabel
                    control={<Switch checked={settings.eval_professional_open === '1'} onChange={handleChange} name="eval_professional_open" disabled={restoring} />}
                    label="ด้านวิชาชีพครู"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControlLabel
                    control={<Switch checked={settings.eval_specialist_open === '1'} onChange={handleChange} name="eval_specialist_open" disabled={restoring} />}
                    label="ด้านวิชาเอก"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControlLabel
                    control={<Switch checked={settings.eval_coteacher_open === '1'} onChange={handleChange} name="eval_coteacher_open" disabled={restoring} />}
                    label="ครูพี่เลี้ยง"
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* ระบบสำรวจความต้องการนักศึกษาฝึกประสบการณ์ */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 1, mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  ระบบสำรวจความต้องการนักศึกษาฝึกประสบการณ์ (Student Teacher Demand Survey)
                </Typography>
                {(() => {
                  const now = new Date();
                  const start = settings.demand_survey_start_date ? new Date(settings.demand_survey_start_date) : null;
                  const end = settings.demand_survey_end_date ? new Date(settings.demand_survey_end_date) : null;
                  if (settings.demand_survey_open === '0') {
                    return <Chip size="small" color="default" label="ปิดรับความต้องการ (สวิตช์ปิด)" />;
                  }
                  if (start && !isNaN(start.getTime()) && now < start) {
                    return <Chip size="small" color="warning" label={`ยังไม่ถึงกำหนดเปิดรับ (เริ่ม ${start.toLocaleDateString('th-TH')} ${start.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })})`} />;
                  }
                  if (end && !isNaN(end.getTime()) && now > end) {
                    return <Chip size="small" color="error" label="สิ้นสุดระยะเวลารับข้อมูลแล้ว" />;
                  }
                  return <Chip size="small" color="success" label="กำลังเปิดรับข้อมูล" />;
                })()}
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                เปิดหรือปิดให้ครูพี่เลี้ยงของแต่ละโรงเรียนสามารถเข้ามากรอกและแก้ไขจำนวนนักศึกษาที่ต้องการในแต่ละสาขาวิชา และข้อมูลความพร้อมสถานศึกษา
              </Typography>
              <FormControlLabel
                control={
                  <Switch 
                    checked={settings.demand_survey_open === '1'} 
                    onChange={handleChange} 
                    name="demand_survey_open" 
                    color="primary"
                    disabled={restoring} 
                  />
                }
                label={settings.demand_survey_open === '1' ? 'เปิดรับความต้องการ (ครูพี่เลี้ยงสามารถกรอกและแก้ไขข้อมูลได้ตามช่วงเวลา)' : 'ปิดรับความต้องการ (ปิดระบบ - ดูได้อย่างเดียว)'}
              />

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="datetime-local"
                    label="วัน-เวลาเริ่มต้นเปิดรับข้อมูล"
                    name="demand_survey_start_date"
                    value={settings.demand_survey_start_date || ''}
                    onChange={handleChange}
                    slotProps={{ inputLabel: { shrink: true } }}
                    helperText="เว้นว่างไว้หากต้องการเปิดทันทีตามสวิตช์หลัก"
                    disabled={restoring || settings.demand_survey_open === '0'}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="datetime-local"
                    label="วัน-เวลาสิ้นสุดปิดรับข้อมูล"
                    name="demand_survey_end_date"
                    value={settings.demand_survey_end_date || ''}
                    onChange={handleChange}
                    slotProps={{ inputLabel: { shrink: true } }}
                    helperText="เว้นว่างไว้หากไม่ต้องการกำหนดเวลาสิ้นสุด"
                    disabled={restoring || settings.demand_survey_open === '0'}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* ระบบตรวจสอบตำแหน่งที่ตั้งข้อสอบ (GPS Geofencing for Exams) */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 1, mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  ระบบตรวจสอบพิกัดตำแหน่งที่ตั้งในการสอบ (Comprehensive Exam GPS Geofencing)
                </Typography>
                <Chip
                  size="small"
                  color={settings.exam_geofence_global_enabled !== '0' ? 'success' : 'default'}
                  label={settings.exam_geofence_global_enabled !== '0' ? 'เปิดใช้งานทั่วทั้งระบบ (ตามแต่ละชุดข้อสอบ)' : 'ปิดการตรวจพิกัดทั่วทั้งระบบ (อนุญาตให้สอบได้ทุกที่)'}
                />
              </Box>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                สวิตช์หลักสำหรับเปิดหรือปิดระบบตรวจสอบพิกัด GPS สำหรับการสอบประมวลผลความรู้ทั่วทั้งระบบเพื่อความยืดหยุ่น (กรณีจัดสอบในห้องคอมพิวเตอร์ที่ไม่มี GPS หรือมีปัญหาสัญญาณดาวเทียม)
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.exam_geofence_global_enabled !== '0'}
                    onChange={(e) => setSettings({ ...settings, exam_geofence_global_enabled: e.target.checked ? '1' : '0' })}
                    name="exam_geofence_global_enabled"
                    color="primary"
                    disabled={restoring}
                  />
                }
                label={
                  settings.exam_geofence_global_enabled !== '0'
                    ? 'เปิดระบบตรวจสอบตำแหน่งที่ตั้ง (ตรวจสอบตามที่แต่ละชุดข้อสอบกำหนด)'
                    : 'ปิดระบบตรวจสอบตำแหน่งที่ตั้งชั่วคราว (ข้ามการตรวจพิกัด GPS ของทุกชุดข้อสอบ)'
                }
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="contained" type="submit" size="large" fullWidth={{ xs: true, sm: false } as any} disabled={restoring}>
                  บันทึกการตั้งค่า
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* SMTP Email Server Settings Card */}
      <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Box>
              <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                การตั้งค่าระบบส่งอีเมล (SMTP Email Configuration)
              </Typography>
              <Typography variant="caption" color="textSecondary">
                ใช้สำหรับการส่งลิงก์รีเซ็ตรหัสผ่านและการแจ้งเตือนอัตโนมัติในระบบ
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<SendIcon />}
            onClick={() => {
              setTestEmailResult({ type: '', text: '' });
              setTestEmailDialogOpen(true);
            }}
          >
            ทดสอบการส่งอีเมล
          </Button>
        </Box>
        <Divider sx={{ mb: 3 }} />

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="SMTP Host"
                name="smtp_host"
                value={settings.smtp_host || ''}
                onChange={handleChange}
                placeholder="เช่น smtp.gmail.com หรือ mail.kpru.ac.th"
                helperText="Mail Server สำหรับส่งอีเมล"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="SMTP Port"
                name="smtp_port"
                value={settings.smtp_port || ''}
                onChange={handleChange}
                placeholder="587 หรือ 465"
                helperText="พอร์ตการเชื่อมต่อ (ปกติ 587 หรือ 465)"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={settings.smtp_secure === '1' || settings.smtp_secure === 'true'}
                    onChange={handleChange}
                    name="smtp_secure"
                  />
                }
                label="ใช้ SSL/TLS (Secure)"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="SMTP Username / Email"
                name="smtp_user"
                value={settings.smtp_user || ''}
                onChange={handleChange}
                placeholder="อีเมลผู้ส่ง เช่น admin@kpru.ac.th"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="password"
                label="SMTP Password / App Password"
                name="smtp_pass"
                value={settings.smtp_pass || ''}
                onChange={handleChange}
                placeholder="รหัสผ่านอีเมล หรือ Google App Password (16 หลัก)"
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="ชื่อและอีเมลผู้ส่ง (Sender From)"
                name="smtp_from"
                value={settings.smtp_from || ''}
                onChange={handleChange}
                placeholder="TPMS KPRU <no-reply@tp.kpru.ac.th>"
                helperText="ชื่อที่จะแสดงในกล่องจดหมายของผู้รับ"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="URL ของระบบ (Base App URL)"
                name="app_url"
                value={settings.app_url || ''}
                onChange={handleChange}
                placeholder="https://tp.kpru.ac.th"
                helperText="ใช้สร้างลิงก์สำหรับกดในอีเมล"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button variant="contained" type="submit" size="large">
                  บันทึกการตั้งค่าอีเมล
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 4 } }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' }, 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' }, 
          gap: 2, 
          mb: 2 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BackupIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>สำรองข้อมูลระบบ (Database Backup)</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
            <Button 
              variant="outlined" 
              component="label" 
              color="secondary" 
              startIcon={<UploadIcon />}
              disabled={restoring}
              fullWidth={{ xs: true, sm: false } as any}
            >
              อัปโหลดไฟล์ SQL เพื่อคืนค่า
              <input type="file" accept=".sql" hidden onChange={handleUploadRestore} />
            </Button>
            <Button 
              variant="outlined" 
              color="info" 
              onClick={handleRunAutoBackup}
              disabled={restoring}
              fullWidth={{ xs: true, sm: false } as any}
            >
              รันระบบสำรองอัตโนมัติทันที
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleCreateBackup}
              disabled={restoring}
              fullWidth={{ xs: true, sm: false } as any}
            >
              สร้างจุดสำรองข้อมูลใหม่
            </Button>
          </Box>
        </Box>

        {/* Automated Backup Info Box */}
        <Box sx={{ p: 2, mb: 3, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#166534', mb: 0.5 }}>
            🕒 ระบบสำรองฐานข้อมูลอัตโนมัติ (Automated Daily Backup & Self-Healing)
          </Typography>
          <Typography variant="body2" sx={{ color: '#15803d' }}>
            ระบบจะทำการสำรองฐานข้อมูลทุกวันเวลา <strong>00:00 น. (เวลาประเทศไทย ICT UTC+7)</strong> พร้อมระบบ <strong>Self-Healing Catch-up</strong> ที่จะตรวจสอบและสำรองข้อมูลย้อนหลังให้อัตโนมัติทันทีเมื่อเปิดเซิร์ฟเวอร์ หากพบว่ายังไม่มีไฟล์สำรองของวันนั้น
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />
        
        <List>
          {backups.map((b: any) => (
            <ListItem 
              key={b.name}
              divider
              secondaryAction={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton 
                    onClick={() => handleConfirmRestore(b.name)} 
                    color="warning" 
                    title="คืนค่าข้อมูลจากจุดนี้"
                    disabled={restoring}
                  >
                    <RestoreIcon />
                  </IconButton>
                  <IconButton 
                    href={b.url} 
                    target="_blank" 
                    color="primary"
                    title="ดาวน์โหลดไฟล์สำรอง"
                    disabled={restoring}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Box>
              }
            >
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{b.name}</Typography>
                    <Chip 
                      label={b.type === 'AUTOMATED' ? 'อัตโนมัติ (Daily 00:00)' : 'จัดการเอง (Manual)'} 
                      size="small" 
                      color={b.type === 'AUTOMATED' ? 'success' : 'default'} 
                      sx={{ height: 20, fontSize: '0.65rem', fontWeight: 500 }} 
                    />
                    {b.formattedSize && (
                      <Chip 
                        label={b.formattedSize} 
                        size="small" 
                        variant="outlined"
                        color="info"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold', fontFamily: 'monospace' }} 
                      />
                    )}
                  </Box>
                } 
                secondary={`สร้างเมื่อ: ${new Date(b.createdAt).toLocaleString('th-TH')}`} 
              />
            </ListItem>
          ))}
          {backups.length === 0 && <Typography color="textSecondary" align="center" sx={{ py: 3 }}>ยังไม่มีไฟล์สำรองข้อมูลในระบบ</Typography>}
        </List>
      </Paper>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={restoreDialogOpen}
        onClose={() => !restoring && setRestoreDialogOpen(false)}
        aria-labelledby="restore-dialog-title"
        aria-describedby="restore-dialog-description"
      >
        <DialogTitle id="restore-dialog-title" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
          ยืนยันการคืนค่าฐานข้อมูล?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="restore-dialog-description">
            คุณกำลังจะกู้คืนระบบและฐานข้อมูลทั้งหมดโดยใช้ไฟล์สำรอง: <strong>{selectedBackupToRestore}</strong>
            <br /><br />
            <span style={{ color: 'red', fontWeight: 'bold' }}>คำเตือน:</span> ข้อมูลปัจจุบันทั้งหมดในฐานข้อมูลจะถูกลบและเขียนทับด้วยข้อมูลจากไฟล์สำรองนี้ทันที คุณแน่ใจหรือไม่ว่าต้องการดำเนินการต่อ?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRestoreDialogOpen(false)} disabled={restoring}>
            ยกเลิก
          </Button>
          <Button onClick={handleExecuteRestore} color="warning" variant="contained" disabled={restoring} autoFocus>
            {restoring ? 'กำลังคืนค่า...' : 'ยืนยันการคืนค่าข้อมูล'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog
        open={testEmailDialogOpen}
        onClose={() => !testEmailLoading && setTestEmailDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon color="primary" /> ทดสอบการเชื่อมต่อส่งอีเมล
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            ระบุอีเมลปลายทางที่ต้องการรับอีเมลทดสอบเพื่อตรวจสอบความถูกต้องของการตั้งค่า SMTP
          </Typography>

          {testEmailResult.text && (
            <Alert severity={testEmailResult.type as any} sx={{ mb: 2 }}>
              {testEmailResult.text}
            </Alert>
          )}

          <TextField
            autoFocus
            fullWidth
            required
            label="อีเมลปลายทางสำหรับทดสอบ"
            type="email"
            placeholder="your-email@gmail.com หรือ admin@kpru.ac.th"
            value={testEmailAddress}
            onChange={(e) => setTestEmailAddress(e.target.value)}
            disabled={testEmailLoading}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTestEmailDialogOpen(false)} disabled={testEmailLoading} color="inherit">
            ปิด
          </Button>
          <Button
            onClick={handleSendTestEmail}
            variant="contained"
            disabled={testEmailLoading || !testEmailAddress.trim()}
          >
            {testEmailLoading ? 'กำลังส่ง...' : 'ส่งอีเมลทดสอบ'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemSettings;
